/**
 * Which bulk actions a segment offers, and what each one actually calls.
 * For Media Devoted | Created By Ciro Autiero
 *
 * The catalog is keyed by SEGMENT because the same words mean different calls
 * depending on where the row sits: "To queue" is a demote from Live, a
 * mark-ready from Waiting, and a bring-back from Eliminated. Keeping that
 * mapping in one table is what stops the bar from firing the wrong endpoint
 * for a row the user selected in a different tab.
 *
 * Deliberately NOT offered: eliminating a queued or waiting element. No
 * endpoint retires a non-live element, and faking it by promoting-then-
 * demoting would touch the tracker twice for a row that was never on it.
 */

import type { LucideIcon } from 'lucide-react';

import {
  IconEliminate,
  IconMoveToLive,
  IconMoveToQueue,
  IconMoveToWaiting,
  type QueueActionTone,
} from '../queue-action-style';

import type { QueueSegment } from '../queue-filters';

export type BulkActionId = 'promote' | 'to-queue' | 'to-waiting' | 'eliminate';

export interface BulkActionDef {
  id: BulkActionId;
  label: string;
  icon: LucideIcon;
  /** Colour of the destination — the row buttons for this verb use the same. */
  tone: QueueActionTone;
  /** Past tense, for the summary toast: "3 moved to queue". */
  past: string;
  destructive?: boolean;
}

const PROMOTE: BulkActionDef = {
  id: 'promote',
  label: 'Move to live now',
  icon: IconMoveToLive,
  tone: 'live',
  past: 'moved live',
};
const TO_QUEUE: BulkActionDef = {
  id: 'to-queue',
  label: 'Move to queue',
  icon: IconMoveToQueue,
  tone: 'queue',
  past: 'moved to queue',
};
const TO_WAITING: BulkActionDef = {
  id: 'to-waiting',
  label: 'Move to waiting',
  icon: IconMoveToWaiting,
  tone: 'waiting',
  past: 'moved to waiting',
};
const ELIMINATE: BulkActionDef = {
  id: 'eliminate',
  label: 'Eliminate',
  icon: IconEliminate,
  tone: 'eliminate',
  past: 'eliminated',
  destructive: true,
};

/**
 * ONE order everywhere: queue → waiting → live → eliminate, which is the order
 * every row already lays its own buttons out in. The bar sits directly above
 * those rows, so listing the same verbs in a different order reads as a
 * different set of actions — you check twice before clicking. Each segment
 * shows a subset of this sequence, never a reshuffle of it.
 */
const BY_SEGMENT: Record<QueueSegment, BulkActionDef[]> = {
  queued: [TO_WAITING, PROMOTE],
  testing: [TO_QUEUE, TO_WAITING, ELIMINATE],
  waiting: [TO_QUEUE, PROMOTE],
  removed: [TO_QUEUE, TO_WAITING, PROMOTE],
};

/** The action a segment uses to park a row in Waiting, if it offers one. */
export function toWaitingActionFor(
  segment: QueueSegment,
): BulkActionDef | undefined {
  return bulkActionsFor(segment).find((a) => a.id === 'to-waiting');
}

export function bulkActionsFor(segment: QueueSegment): BulkActionDef[] {
  return BY_SEGMENT[segment] ?? [];
}

/** What a (segment, action) pair does to one element. */
export type BulkOp =
  | { kind: 'promote' }
  /** Revive a tombstone, THEN promote — the promote guard refuses a removed
   *  element, so the two steps cannot be collapsed into one call. */
  | { kind: 'revive-and-promote' }
  | { kind: 'demote'; to: 'queue' | 'waiting' | 'eliminated' }
  | { kind: 'requeue'; to: 'queue' | 'waiting' }
  | { kind: 'mark-ready'; ready: boolean };

const OPS: Record<QueueSegment, Partial<Record<BulkActionId, BulkOp>>> = {
  queued: {
    promote: { kind: 'promote' },
    'to-waiting': { kind: 'mark-ready', ready: false },
  },
  testing: {
    'to-queue': { kind: 'demote', to: 'queue' },
    'to-waiting': { kind: 'demote', to: 'waiting' },
    eliminate: { kind: 'demote', to: 'eliminated' },
  },
  waiting: {
    promote: { kind: 'promote' },
    'to-queue': { kind: 'mark-ready', ready: true },
  },
  removed: {
    promote: { kind: 'revive-and-promote' },
    'to-queue': { kind: 'requeue', to: 'queue' },
    'to-waiting': { kind: 'requeue', to: 'waiting' },
  },
};

export function bulkOpFor(
  segment: QueueSegment,
  action: BulkActionId,
): BulkOp | undefined {
  return OPS[segment]?.[action];
}
